import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { differenceInMilliseconds } from 'date-fns';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
/* --- import components ----*/
import {
  Container,
  MultipleInputWrapper,
  SimpleInput,
  SingleInputWrapper,
  SubmitButton,
} from 'src/components/Form/newElements';
import { InputPhoneEmailForm, Label } from 'src/components/Form/elements';
import ErrorMessage from 'src/components/Form/ErrorMessage';
import SuccessMessage from 'src/components/Form/SuccessMessage';
import { flags } from '../../components/Icons/Flags';
/* --- import Style ----*/
import './checkboxTheme.css';
import 'react-input-checkbox/lib/react-input-checkbox.min.css';
import { mediaQueries } from 'src/style/breakpoints';
import styled from 'src/style';
import CheckboxComp from './CheckboxComp';

const initialValues = {
  utmSource: Cookies.get('tracking') || Cookies.get('utm_source') || '',
  gclid: Cookies.get('_gcl_aw') || '',
  fbclid: Cookies.get('fbclid') || '',
  referrer: Cookies.get('referrer') || '',
};

const FormContainer = styled.form({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '& h2::before': {
    width: 26,
    height: 6,
    marginRight: 12,
    marginTop: 14,
    borderRadius: 50,
    backgroundColor: '#FCC01A',
    marginTop: 12,
    minWidth: 22,
    display: 'block',
  },
  '& input': {
    textAlign: 'center',
  },
  '& button': {
    margin: 'auto',
  },
  [mediaQueries.phoneOnly]: {
    margin: '0 auto',
  },
});

const EmailForm = ({ submitButtonDefaultText, route, location, datalayerTracking, originPage }) => {
  const MINIMUM_INTERVAL_BETWEEN_2_REQUESTS_IN_MS = 1000;
  const [submitButtonIsDisabled, setSubmitButtonIsDisabled] = useState(true);
  const [submitButtonText, setSubmitButtonText] = useState(submitButtonDefaultText);
  const [checkboxIsChecked, setCheckboxIsChecked] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [errorEmail, setErrorEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorPhoneNumber, setErrorPhoneNumber] = useState('');
  const [lastTimeSubmitted, setLastTimeSubmitted] = useState(new Date().getTime());
  const [pathname, setPathname] = useState('');
  const canFetchData = () => {
    const now = new Date().getTime();
    if (
      differenceInMilliseconds(now, lastTimeSubmitted) > MINIMUM_INTERVAL_BETWEEN_2_REQUESTS_IN_MS
    ) {
      setLastTimeSubmitted(now);
      return true;
    }
    return false;
  };

  const getLocalization = async () => {
    try {
      const { data: localizationData } = await axios.get(`https://geolocation-db.com/json/`, {
        timeout: 5000,
      });
      return localizationData;
    } catch (error) {
      console.log({ error });
      return {
        postal: undefined,
        city: undefined,
        country_name: undefined,
        country_code: undefined,
        IPv4: undefined,
      };
    }
  };

  const resetForm = (message) => {
    setSuccess(message);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhoneNumber('');
    setCheckboxIsChecked(false);
    setError(null);
  };

  const regexEmail = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const isPossibleEmail = regexEmail.test(email);

  const sendData = async () => {
    setSubmitButtonText('Envoi en cours...');
    const localizationData = await getLocalization();
    try {
      const { data, error } = await axios.post(`${process.env.GATSBY_SPUTNIK_API}${route}`, {
        firstName,
        lastName,
        email,
        phoneNumber: originPage === 'freeTraining' ? phoneNumber : null,
        ...initialValues,
        ...localizationData,
        source: document.location.href,
        track: {
          utmSource: Cookies.get('utm_source') || '',
          utmMedium: Cookies.get('utm_medium') || '',
          utmCampaign: Cookies.get('utm_campaign') || '',
          utmCampaignId: Cookies.get('utm_campaign_id') || '',
          adGroupName: Cookies.get('adgroup_name') || '',
          utmAdGroupId: Cookies.get('utm_adgroup_id') || '',
          utmTerm: Cookies.get('utm_term') || '',
          matchType: Cookies.get('matchtype') || '',
          utmContent: Cookies.get('utm_content') || '',
        },
      });
      if (data.success) {
        resetForm(data.success);
        if (datalayerTracking) {
          datalayerTracking();
        }
      } else if (error || data?.error) {
        setError(error || data?.error);
        setSuccess(null);
      } else {
        setError('😨 Erreur inconnue. Veuillez nous contacter sur contact@lereacteur.io.');
        setSuccess(null);
      }
    } catch (e) {
      setError(e.response.data.error);
      setSuccess(null);
      console.log({ e });
    }

    setSubmitButtonText(submitButtonDefaultText);
  };

  const handleChange = (e) => {
    const name = e.target.name;
    if (name === 'firstName') {
      setFirstName(e.target.value);
    } else if (name === 'lastName') {
      setLastName(e.target.value);
    } else if (name === 'email') {
      setEmail(e.target.value);
      setErrorEmail('');
    }
  };

  const handlePhoneChange = (e) => {
    setPhoneNumber(e);
    setErrorPhoneNumber('');
  };

  useEffect(() => {
    const pathnameArray = location.pathname.split('/');
    pathnameArray.length > 2 && setPathname(pathnameArray[pathnameArray.length - 1]);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      originPage === 'freeTraining' &&
      (!isPossiblePhoneNumber(phoneNumber) || !isPossibleEmail)
    ) {
      if (!isPossiblePhoneNumber(phoneNumber)) {
        setErrorPhoneNumber('Numéro de téléphone non valide');
      }
      if (!isPossibleEmail) {
        setErrorEmail('Email non valide');
      }
      return;
    } else if (!isPossibleEmail) {
      setErrorEmail('Email non valide');
      return;
    }
    if (canFetchData()) {
      sendData();
    } else {
      console.log('Slow down bro !');
    }
  };

  // useEffect(() => {
  //   console.log({ lastTimeSubmitted });
  // }, [lastTimeSubmitted]);

  const handleCheckbox = () => {
    setCheckboxIsChecked(!checkboxIsChecked);
  };

  useEffect(() => {
    setError(null);
    if (
      (originPage === 'freeTraining' &&
        email &&
        firstName &&
        lastName &&
        phoneNumber &&
        checkboxIsChecked &&
        !error &&
        !errorEmail &&
        !errorPhoneNumber) ||
      (originPage === undefined &&
        email &&
        firstName &&
        lastName &&
        checkboxIsChecked &&
        !error &&
        !errorEmail)
    ) {
      setSubmitButtonIsDisabled(false);
    } else {
      setSubmitButtonIsDisabled(true);
    }
  }, [email, firstName, lastName, phoneNumber, errorEmail, errorPhoneNumber, checkboxIsChecked]);

  return (
    <Container css={{ marginTop: '50px' }}>
      <FormContainer autocomplete="off" onSubmit={handleSubmit}>
        <input type="hidden" name="pathname" value={pathname} />
        <MultipleInputWrapper>
          <div css={{ marginBottom: 30 }}>
            <Label>Prénom *</Label>
            <SimpleInput
              name="firstName"
              type="text"
              onChange={handleChange}
              value={firstName}
              autoFocus={false}
              required={true}
              placeholder="Neil"
            />
          </div>
          <div
            css={{
              [mediaQueries.phoneOnly]: {
                marginBottom: 30,
              },
            }}
          >
            <Label>Nom *</Label>
            <SimpleInput
              name="lastName"
              type="text"
              onChange={handleChange}
              value={lastName}
              autoFocus={false}
              required={true}
              placeholder="Armstrong"
            />
          </div>
        </MultipleInputWrapper>

        {originPage === 'freeTraining' ? (
          <MultipleInputWrapper>
            <div className="w-1/2">
              <Label>Email *</Label>
              <SimpleInput
                name="email"
                type="text"
                onChange={handleChange}
                value={email}
                autoFocus={false}
                required={true}
                placeholder="neil.armstrong@nasa.gov"
                css={{ marginBottom: errorEmail ? 5 : 30 }}
              />
              <ErrorMessage
                isVisible={errorEmail}
                message={errorEmail}
                css={{ marginBottom: 30 }}
              />
            </div>
            <div
              className="w-1/2"
              css={{
                [mediaQueries.phoneOnly]: {
                  marginBottom: errorPhoneNumber ? 5 : 30,
                },
              }}
            >
              <Label>Téléphone *</Label>
              <InputPhoneEmailForm
                name="phoneNumber"
                placeholder="Entrez votre numéro"
                autoComplete="tel"
                country="FR"
                defaultCountry="FR"
                flags={flags}
                displayInitialValueAsLocalNumber={true}
                value={phoneNumber}
                onChange={handlePhoneChange}
              />
              <ErrorMessage
                isVisible={errorPhoneNumber}
                message={errorPhoneNumber}
                css={{ marginBottom: 30 }}
              />
            </div>
          </MultipleInputWrapper>
        ) : (
          <SingleInputWrapper>
            <Label>Email *</Label>
            <SimpleInput
              name="email"
              type="text"
              onChange={handleChange}
              value={email}
              autoFocus={false}
              required={true}
              placeholder="neil.armstrong@nasa.gov"
              css={{ marginBottom: errorEmail ? 5 : 30 }}
            />
            <ErrorMessage isVisible={errorEmail} message={errorEmail} css={{ marginBottom: 30 }} />
          </SingleInputWrapper>
        )}

        <div style={{ marginBottom: 20 }}>
          <CheckboxComp
            text="J'accepte de recevoir par mail les communications du Reacteur."
            value={checkboxIsChecked}
            onChange={handleCheckbox}
          />
        </div>
        <ErrorMessage isVisible={error && true} message={error} css={{ marginBottom: 20 }} />
        <SuccessMessage
          isVisible={success && !error}
          message={success}
          css={{ marginBottom: 20 }}
        />
        <SubmitButton
          type="submit"
          disabled={submitButtonIsDisabled}
          disabledStyle={submitButtonIsDisabled}
          style={{ width: '100%' }}
        >
          {submitButtonText}
        </SubmitButton>
      </FormContainer>
    </Container>
  );
};
export default EmailForm;
